import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../context/userContext";
import Input from "../props/inputProp";
import { postData } from "../fetchRoutes/postData";
import { Eye, EyeOff } from "lucide-react"; 

export default function LoginForm() {
    const navigate = useNavigate();
    const location = useLocation();

    const loginUrl = process.env.REACT_APP_LOGIN_USER;
    const resetPasswordUrl = process.env.REACT_APP_RESET_PASSWORD;
    const from = location.state?.from || "/"; // fallback to homepage

    const [errorMessage, setErrorMessage] = useState('');
    const [forgotPassword, setForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [passwordResetError, setPasswordResetError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const { setUserDetails, setIsLoggedIn } = useContext(UserContext);

    const [user, setUser] = useState({
        email: '',
        password: ''
    });

    const handleChange = (event) => {
        const { name, value } = event.target;
        setUser({ ...user, [name]: value });
    };

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSignUp = () => {
        navigate("/account/register");
    };

    const handleResetEmailChange = (event) => {
        setResetEmail(event.target.value);
    };

    const handleForgotPassword = () => {
        setSuccessMessage('');
        setPasswordResetError('');
        setForgotPassword(true);
    };

    const handleResetPassword = async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await postData(resetPasswordUrl, { resetEmail });
            if (response.status.toLowerCase() === 'success') {
                setSuccessMessage("Password reset link has been sent to your email!");
                setTimeout(() => {
                    setSuccessMessage('');
                    setForgotPassword(false);
                    setPasswordResetError("");
                }, 2000);
                setResetEmail('');
            } else {
                setPasswordResetError('Error sending reset password link. Please try again');
                setTimeout(() => setPasswordResetError(''), 3000);
            }
        } catch (error) {
            setPasswordResetError("Email not found. Sign up to create an account!");
            setTimeout(() => setPasswordResetError(''), 3000);
        } finally {
            setLoading(false);
        }
    };
    
    const handleSubmit = (event) => {
        event.preventDefault();
        setLoading(true);
        
        if (!validateEmail(user.email)) {
            setErrorMessage("Invalid email format.");
            return;
        }
        if (user.password.length < 8) {
            setErrorMessage("Password must be at least 8 characters long.");
            return;
        }
        setErrorMessage('');

        (async () => {
            try {
                const response = await postData(loginUrl, user);
                if (response.status.toLowerCase() === 'success') {
                    setUserDetails({
                        name: response.user.name,
                        email: response.user.email,
                        username: response.user.username,
                        role: response.user.role,
                        savedProperties: response.user.savedProperties,
                        notifification: response.user.notification,
                        image: response.user.image,
                        _id: response.user._id
                    });
                    navigate(from, { replace: true });
                    // navigate('/');
                    setIsLoggedIn(true);
                }
            } catch (err) {
                setErrorMessage('Incorrect email or password. Please try again');
            }finally{
                setLoading(false);
            }
        })();
    };

    return (
        <main className="home loginPage">
            {loading && (
                <div className="loaderContainer">
                    <div className="loader"></div>
                    <p className="loadingText">Logging you in...</p>
                </div>
            )}
            <div className="loginContainer">
                <div className="loginLogoContainer">
                    <div className="formTitle">
                        <p className="logInTitle">Your Next Move Starts Here</p>
                    </div>
                </div>
                <div className="loginFormContainer">
                    <form className="loginForm" onSubmit={handleSubmit}>
                        <div className="inputs">
                            <label className="label">Email<span className="asterisk"> *</span></label>
                            <Input
                                className="email"
                                type="email"
                                name="email"
                                placeholder="this@example.com"
                                autoCapitalize="off"
                                autoCorrect="off"
                                required
                                onChange={handleChange}
                            />
                        </div>

                        <div className="inputs">
                            <label className="label passwordInputs">
                                <div className="passwordLabelDetail">
                                    Password<span className="asterisk"> *</span>
                                </div>
                                <div className="eyeContainer">
                                    <span 
                                        className="showHideIcon" 
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </span>
                                </div>
                               
                            </label>
                            <Input
                                className="password"
                                type={showPassword ? "text" : "password"}
                                name="password"
                                placeholder="........"
                                autoCapitalize="off"
                                autoCorrect="off"
                                required
                                minLength="8"
                                onChange={handleChange}
                            />
                        </div>

                        <div className="forgotPassword" onClick={handleForgotPassword}>
                            <p className="forgotPasswordMessage">Forgot password?</p>
                        </div>

                        <div className="errorMessage">
                            <span className="errorMessage">{errorMessage}</span>
                        </div>

                        <div className="lButton">
                            <button className="loginButton" type="submit"> Log In </button>
                        </div>
                    </form>
                    <div className="register">
                        <span className="registerNow">New here?</span>
                        <button className="signUp" onClick={handleSignUp}>
                            Sign Up!
                        </button>
                    </div>
                    <div className="cookieMessage">
                        <span className="cookie">By logging in, you agree to our use of cookies to keep you signed in and enhance your experience.</span>
                    </div>
                </div>
            </div>

            {forgotPassword && (
                <div className="forgotPasswordModal">
                    <div className="forgotPasswordContainer">
                        <form onSubmit={handleResetPassword}>
                            <h3>Reset Password</h3>
                            <Input 
                                type="email" 
                                name="resetEmail" 
                                placeholder="example@gmail.com" 
                                value={resetEmail} 
                                onChange={handleResetEmailChange} 
                                required />
                            <div className="errorMessage passwordError">
                                <span className={successMessage ? 'successMessage' : ''}>{passwordResetError || successMessage}</span>
                            </div>
                            <button className="sendLink" type="submit" disabled={loading}>
                                {loading ? "Sending..." : "Send Password Reset Link"}
                            </button>
                            <button className="cancelButton" onClick={() => setForgotPassword(false)}>Cancel</button>
                        </form>
                        {loading && (
                            <div className="loaderContainer">
                                <div className="spinner"></div>
                                <p className="loadingText">Generating reset link...</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </main>
    );
}
